import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { CheckCircle, ContentCopy, PostAdd } from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  SubmissionPartnerSummary,
  getPartnerFilterOptions,
  listSubmissionPartner,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";

import SubmissionListView, {
  PaginationModel,
} from "@/components/online-reporting/SubmissionListView";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import {
  PersistentFilterValidators,
  usePersistentFilters,
} from "@/hooks/usePersistentFilters";
import { IUserPermission } from "@/types/typesAlerts";
import { ReportStatus, reportColourTypes } from "@/types/typesReportV2";

import {
  PartnerFilterOptions,
  PartnerFilters,
  PartnerReportFilterChips,
  PartnerReportFilters,
  customSelectionsStillValid,
  emptyPartnerFilters,
  partnerFiltersToQuery,
} from "./PartnerReportFilters";
import {
  partnerStatusOptions,
  partnerStatusTooltips,
  partnerStatusTypes,
} from "./partnerStatusLabels";

type PartnerReportRow = SubmissionPartnerSummary & {
  id: string;
};

const PAGE_SIZE = 8;

// Mirrors the backend's `sort_by` allowlist in api-rs `partner_submission.rs`.
const PARTNER_SORT_FIELDS = [
  "status",
  "store_number",
  "tracking_number",
  "lcbo_incident_number",
  "has_supplemental",
  "le_agency",
  "incident_number",
  "incident_start_time",
  "time_submitted",
];

const toRow = (summary: SubmissionPartnerSummary): PartnerReportRow => ({
  ...summary,
  id: summary.submission_id,
});

// Clicking a copyable cell copies its value rather than opening the report.
const renderCopyableCell = (params: GridRenderCellParams) => {
  const value = params.value as string | null | undefined;
  if (!value) return null;
  return (
    <Tooltip title="Click to copy" arrow>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value);
          toast.success("Copied to clipboard");
        }}
        sx={{
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          "&:hover .copy-icon": { opacity: 1 },
        }}
      >
        {value}
        <ContentCopy
          className="copy-icon"
          fontSize="inherit"
          sx={{ opacity: 0.3, color: "text.secondary" }}
        />
      </Box>
    </Tooltip>
  );
};

const PartnerReportList = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { isDevEnv } = useDevEnvironment();
  const { currentUser } = useContext(AuthStateContext);

  const permissions = currentUser
    ? parseUserPermissions(currentUser.permissions as IUserPermission[])
    : null;
  const canAddSupplemental = permissions?.canAddPartnerSupplemental ?? false;

  const [rows, setRows] = useState<PartnerReportRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters, validateFilters] =
    usePersistentFilters<PartnerFilters>({
      storageKey: "partnerReportFilters",
      orgId,
      defaultValue: emptyPartnerFilters,
      persistentKeys: [
        "status",
        "leAgency",
        "reportType",
        "hasSupplemental",
        "custom",
        "sortBy",
        "sortOrder",
      ],
    });
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filterOptions, setFilterOptions] = useState<PartnerFilterOptions>({
    agencies: [],
    reportTypes: [],
    customFilters: [],
  });
  const [customFiltersLoaded, setCustomFiltersLoaded] = useState(false);

  // Reset a persisted filter once live option data shows it's no longer valid.
  useEffect(() => {
    const validators: PersistentFilterValidators<PartnerFilters> = {
      status: (value) =>
        value
          .split("|")
          .every((status) => partnerStatusOptions.includes(status)),
      leAgency: (value) =>
        filterOptions.agencies.length === 0 ||
        value
          .split("|")
          .every((id) => filterOptions.agencies.some((a) => a.id === id)),
      reportType: (value) =>
        filterOptions.reportTypes.length === 0 ||
        value
          .split("|")
          .every((surveyClass) =>
            filterOptions.reportTypes.some(
              (t) => t.survey_class === surveyClass,
            ),
          ),
      custom: (value) =>
        !customFiltersLoaded ||
        customSelectionsStillValid(value, filterOptions.customFilters),
      sortBy: (value) => PARTNER_SORT_FIELDS.includes(value),
      sortOrder: (value) => value === "asc" || value === "desc",
    };
    validateFilters(validators);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  // Agency + report-type dropdown options, scoped to this partner's reports.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getPartnerFilterOptions({ query: { partner_org_id: orgId } })
      .then((res) => {
        if (!active || !res.data) return;
        setFilterOptions({
          agencies: res.data.agencies,
          reportTypes: res.data.report_types,
          customFilters: res.data.custom_filters,
        });
        setCustomFiltersLoaded(true);
      })
      .catch(() => {
        /* filters still usable without options; the list just won't prefill them */
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  // Guards against a slow in-flight request overwriting a newer one: each call takes the next id
  // and only applies its result/loading state while it's still the latest.
  const requestIdRef = useRef(0);
  const fetchReports = useCallback(async () => {
    if (!orgId) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const response = (
        await listSubmissionPartner({
          query: {
            ...partnerFiltersToQuery(filters),
            search: searchText.trim().length > 0 ? searchText : null,
            sort_by: filters.sortBy,
            sort_order: filters.sortOrder,
            partner_org_id: orgId,
            limit: paginationModel.pageSize,
            offset: paginationModel.page * paginationModel.pageSize,
          },
        })
      ).data;
      if (requestId !== requestIdRef.current) return;
      setRows((response?.submissions ?? []).map(toRow));
      setTotalCount(response?.total_count ?? 0);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      const message = e instanceof Error ? e.message : "";
      toast.error(`Failed to fetch reports${isDevEnv ? `: ${message}` : ""}`);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [searchText, filters, paginationModel, isDevEnv, orgId]);

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void fetchReports(), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchReports]);

  const columns: GridColDef[] = [
    {
      field: "status",
      headerName: "Status",
      description: "Current review status of the report.",
      width: 120,
      sortable: true,
      filterable: false,
      renderCell: (params) => {
        const status = params.value as ReportStatus;
        return (
          <Tooltip title={partnerStatusTooltips[status] || ""} arrow>
            <Chip
              label={partnerStatusTypes[status] ?? status}
              color={reportColourTypes[status] ?? "default"}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "store_number",
      headerName: "Store #",
      description: "LCBO store number where the incident occurred.",
      width: 110,
      sortable: true,
      filterable: false,
      renderCell: renderCopyableCell,
    },
    {
      field: "tracking_number",
      headerName: "Rubicon #",
      description: "Rubicon reference number for this report.",
      width: 150,
      sortable: true,
      filterable: false,
      renderCell: renderCopyableCell,
    },
    {
      field: "lcbo_incident_number",
      headerName: "Incident #",
      description: "Store-provided incident number.",
      width: 140,
      sortable: true,
      filterable: false,
      renderCell: renderCopyableCell,
    },
    {
      field: "has_supplemental",
      headerName: "Supplemental",
      description: "Whether a supplemental report is attached; add one if not.",
      width: 130,
      filterable: false,
      align: "center",
      renderCell: (params) => {
        if (params.value) {
          return (
            <Tooltip title="Supplemental attached" arrow>
              <CheckCircle color="success" />
            </Tooltip>
          );
        }
        const url = (params.row as PartnerReportRow).supplemental_url;
        if (!canAddSupplemental || !url) return null;
        return (
          <Tooltip title="Add supplemental" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              <PostAdd />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "le_agency",
      headerName: "Police",
      description: "The law-enforcement agency the report was submitted to.",
      width: 150,
      sortable: true,
      filterable: false,
    },
    {
      field: "incident_number",
      headerName: "Police #",
      description: "Police occurrence / incident number.",
      width: 140,
      sortable: true,
      filterable: false,
      renderCell: renderCopyableCell,
    },
    {
      field: "incident_start_time",
      headerName: "Incident Start Time",
      description: "When the incident began, from the report answers.",
      width: 180,
      sortable: true,
      filterable: false,
      type: "dateTime",
      valueGetter: (value) => (value ? new Date(value as string) : undefined),
    },
    {
      field: "time_submitted",
      headerName: "Time Submitted",
      description: "When the report was submitted to Rubicon.",
      width: 180,
      sortable: true,
      filterable: false,
      type: "dateTime",
      valueGetter: (value) => (value ? new Date(value as string) : undefined),
    },
  ];

  if (!orgId) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
        Select a partner organization to view its reports.
      </Box>
    );
  }

  return (
    <>
      <SubmissionListView<PartnerReportRow>
        rows={rows}
        columns={columns}
        rowCount={totalCount}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        searchText={searchText}
        setSearchText={(nextSearchText) => {
          setSearchText(nextSearchText);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        sortModel={
          filters.sortBy
            ? [{ field: filters.sortBy, sort: filters.sortOrder }]
            : []
        }
        onSortModelChange={([item]) => {
          setFilters({
            ...filters,
            sortBy: item?.field ?? null,
            sortOrder: item?.sort === "asc" ? "asc" : "desc",
          });
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        filtersVisible={filtersVisible}
        setFiltersVisible={setFiltersVisible}
        filterChips={
          <PartnerReportFilterChips
            filters={filters}
            setFilters={(next) => {
              setFilters(next);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            options={filterOptions}
          />
        }
        renderFilterPanel={(anchorEl) => (
          <PartnerReportFilters
            isOpen={filtersVisible}
            anchorEl={anchorEl}
            onClose={() => setFiltersVisible(false)}
            filters={filters}
            setFilters={(next) => {
              setFilters(next);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            options={filterOptions}
          />
        )}
        onRowClick={(row) =>
          navigate(`/rubipartner/${orgId}/report/${row.id}/condensed`)
        }
        // Reports with a supplemental attached get a greyed background to deprioritise them.
        getRowClassName={(params) =>
          params.row.has_supplemental ? "row-deprioritized" : ""
        }
        sx={{
          "& .row-deprioritized": {
            backgroundColor: "action.hover",
            "&:hover": { backgroundColor: "action.selected" },
          },
        }}
      />
    </>
  );
};

export default PartnerReportList;
